import clsx from "clsx";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { useMemo } from "react";

interface CheckOption {
	id: string | number;
	value: string;
	label?: string;
	icon?: React.ReactNode;
}

interface CheckGroupFieldProps {
	id: string;
	label: string;
	options: CheckOption[];
	// Controlled multi-select
	values?: string[];
	onChange?: (values: string[]) => void;
	required?: boolean;
	error?: string;
	disabled?: boolean;
	className?: string;
	gridCols?: number;
	showIcons?: boolean;
	disabledOptions?: string[];
	navRef?: boolean;
}

export function CheckGroupField({
	id,
	label,
	options,
	values,
	onChange,
	error,
	required = false,
	disabled = false,
	className = "",
	gridCols = 2,
	showIcons = false,
	disabledOptions = [],
	navRef = false,
}: CheckGroupFieldProps) {
	const checkAll = useMemo(() => new Set(["any", "everything"].map((v) => v.toLowerCase())), []);
	const selectedValues = values !== undefined && values.length > 0 ? values : [];
	const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

	const colClass =
		{
			1: "jF-grid-cols-1",
			2: "jF-grid-cols-2",
			3: "jF-grid-cols-3",
			4: "jF-grid-cols-4",
			5: "jF-grid-cols-5",
			6: "jF-grid-cols-6",
		}[gridCols] || "jF-grid-cols-2";

	const handleChange = (next: string[]) => {
		onChange?.(next);
	};

	const disabledSet = useMemo(() => new Set(disabledOptions), [disabledOptions]);

	// Map each option value to its original index for stable ordered selections
	const valueToIndex = useMemo(() => {
		const map = new Map<string, number>();
		options.forEach((opt, i) => map.set(opt.value, i));
		return map;
	}, [options]);

	return (
		<div className={className}>
			<div
				role="group"
				aria-label={label}
				aria-required={required}
				aria-describedby={error ? `${id}-error` : ""}
				aria-invalid={!!error}
				aria-disabled={disabled}
				id={id}
			>
				<div className="jF-label">{label}</div>
				<div
					className={clsx(
						navRef ? "jF-grid-cols-2" : colClass,
						`jF-grid jF-mt-1 jF-gap-x-2 jF-gap-y-2 md:jF-gap-x-2`
					)}
				>
					{options.map((item, index) => {
						const isOptionDisabled = disabled || disabledSet.has(item.value);
						const selected = selectedSet.has(item.value);
						const toggle = () => {
							if (isOptionDisabled) return;

							// Toggle via Set for O(1) membership
							const nextSet = new Set(selectedValues);
							if (selected) {
								nextSet.delete(item.value);
							} else {
								nextSet.add(item.value);
							}

							let newValues = Array.from(nextSet);

							// Check if the option is a CheckAll option
							const hasCheckAll = newValues.some((value) =>
								checkAll.has(value.toLowerCase())
							);
							if (hasCheckAll && checkAll.has(item.value.toLowerCase())) {
								newValues = [item.value];
							} else if (hasCheckAll) {
								newValues = newValues.filter((v) => !checkAll.has(v.toLowerCase()));
							}

							// Return values sorted according to its order.
							const sortedValues = [...newValues].sort((a, b) => {
								const ai = valueToIndex.get(a) ?? 0;
								const bi = valueToIndex.get(b) ?? 0;
								return ai - bi;
							});
							handleChange(sortedValues);
						};
						return (
							<div
								key={item.value}
								id={`${index}-option-${item.id}`}
								role="button"
								aria-pressed={selected}
								aria-disabled={isOptionDisabled}
								tabIndex={isOptionDisabled ? -1 : 0}
								onClick={toggle}
								className={clsx(
									isOptionDisabled
										? "jF-opacity-50 jF-cursor-not-allowed"
										: "jF-cursor-pointer",
									isOptionDisabled ? "jF-bg-gray-50" : "jF-bg-white",
									selected
										? "jF-border-[--jF-theme-color] jF-ring-1 jF-ring-[--jF-theme-color]"
										: "jF-border-gray-300",
									"jF-relative jF-flex jF-flex-1 jF-rounded-lg jF-border jF-p-2 jF-shadow-sm focus:jF-outline-none focus-visible:jF-outline-[--jF-theme-color] md:jF-p-3"
								)}
							>
								<span className="jF-flex jF-flex-1">
									<span
										className={clsx(
											navRef ? "" : "",
											"jF-flex jF-flex-col jF-items-start jF-justify-center"
										)}
									>
										{showIcons && item.icon && (
											<div
												className={clsx(
													!selected
														? " jF-text-gray-900"
														: "jF-text-[--jF-theme-color]"
												)}
											>
												{item.icon}
											</div>
										)}
										<span
											className={clsx(
												!selected
													? "jF-flex jF-text-sm jF-font-medium jF-text-gray-900"
													: "",
												"jF-text-wrap jF-text-[--jF-theme-color] jF-flex jF-text-sm jF-font-medium jF-leading-4"
											)}
										>
											{item.label || item.value}
										</span>
									</span>
								</span>
								<CheckCircleIcon
									className={clsx(
										!selected ? "jF-invisible" : "",
										"jF-h-5 jF-w-5 jF-text-[--jF-theme-color]"
									)}
									aria-hidden="true"
									weight="fill"
								/>
								<span
									className={clsx(
										selected
											? "jF-border-[--jF-theme-color]"
											: "jF-border-transparent",
										"jF-pointer-events-none jF-absolute jF-rounded-lg"
									)}
									aria-hidden="true"
								/>
							</div>
						);
					})}
				</div>
			</div>
			{error && required && (
				<p
					id={`${id}-error`}
					className="jF-mt-1 jF-text-xs jF-text-red-600"
					role="alert"
					aria-live="polite"
				>
					{error}
				</p>
			)}
		</div>
	);
}
